// Shared privacy policy for ALL ecosquirrel tools and the landing page.
// Single source of truth: edit here, it is synced into each app's src/pages/ on
// build by scripts/gen-tokens.mjs. Language is derived from the route.
//
// Note: this is a solid template covering the actual data flows (local-only
// generation, hosting logs, AdSense behind Google's CMP). Have it reviewed by
// counsel before relying on it commercially.
import { Container, List, Text, Title } from '@mantine/core';
import { useLocation } from 'react-router-dom';

const OPERATOR = 'Matthias Schaper, Unterstr. 138, 53859 Niederkassel, Deutschland · info@ecosquirrel.de';

export function DatenschutzPage() {
  const { pathname } = useLocation();
  const en = pathname === '/en' || pathname.startsWith('/en/');

  if (en) {
    return (
      <Container size="sm" py="xl">
        <Title order={1} mb="lg">
          Privacy Policy
        </Title>

        <Title order={2} mt="lg" mb="xs">
          Controller
        </Title>
        <Text mb="md">{OPERATOR}</Text>

        <Title order={2} mt="lg" mb="xs">
          Processing in your browser
        </Title>
        <Text mb="md">
          All tools (passwords, keys, hashes, conversions, etc.) run exclusively in your browser via
          the Web Crypto API. No passwords, keys or input data are transmitted to a server or stored
          by us. Close the tab and nothing remains.
        </Text>

        <Title order={2} mt="lg" mb="xs">
          Hosting and server log files
        </Title>
        <Text mb="md">
          The site is served as static files via Caddy on a server in the EU (TLS via Let&apos;s
          Encrypt). When you access the site, the server processes technically necessary data (IP
          address, date and time, requested resource, referrer, user agent) to deliver the content
          and ensure security. Legal basis: legitimate interest, Art. 6(1)(f) GDPR.
        </Text>

        <Title order={2} mt="lg" mb="xs">
          Google AdSense and consent management (CMP)
        </Title>
        <Text mb="md">
          We use Google AdSense (Google Ireland Limited) to fund the tools. Consent is collected and
          stored by Google&apos;s certified Consent Management Platform (Funding Choices); we do not
          operate our own cookie banner. Advertising cookies and related processing are only set
          after your explicit consent, Art. 6(1)(a) GDPR. You can revoke or change your choice at
          any time via the &quot;Privacy options&quot; / &quot;Manage consent&quot; control that
          Google&apos;s CMP displays on the site. Further information is in Google&apos;s privacy
          policy at https://policies.google.com/privacy.
        </Text>

        <Title order={2} mt="lg" mb="xs">
          Your rights
        </Title>
        <List mb="md">
          <List.Item>Access, rectification and erasure</List.Item>
          <List.Item>Restriction of processing and data portability</List.Item>
          <List.Item>Objection to processing and withdrawal of consent</List.Item>
          <List.Item>Complaint to a supervisory authority</List.Item>
        </List>

        <Text size="sm" c="dimmed" mt="xl">
          Last updated: June 2026.
        </Text>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Title order={1} mb="lg">
        Datenschutzerklärung
      </Title>

      <Title order={2} mt="lg" mb="xs">
        Verantwortlicher
      </Title>
      <Text mb="md">{OPERATOR}</Text>

      <Title order={2} mt="lg" mb="xs">
        Verarbeitung im Browser
      </Title>
      <Text mb="md">
        Alle Werkzeuge (Passwörter, Schlüssel, Hashes, Konvertierungen usw.) laufen ausschließlich in
        deinem Browser über die Web Crypto API. Es werden keine Passwörter, Schlüssel oder
        Eingabedaten an einen Server übertragen oder von uns gespeichert. Schließt du den Tab, bleibt
        nichts zurück.
      </Text>

      <Title order={2} mt="lg" mb="xs">
        Hosting und Server-Logfiles
      </Title>
      <Text mb="md">
        Die Seite wird als statische Dateien über Caddy auf einem Server in der EU ausgeliefert (TLS
        per Let&apos;s Encrypt). Beim Aufruf verarbeitet der Server technisch notwendige Daten
        (IP-Adresse, Datum und Uhrzeit, abgerufene Ressource, Referrer, User-Agent) zur Auslieferung
        und Sicherheit. Rechtsgrundlage ist das berechtigte Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO.
      </Text>

      <Title order={2} mt="lg" mb="xs">
        Google AdSense und Einwilligungsverwaltung (CMP)
      </Title>
      <Text mb="md">
        Zur Finanzierung nutzen wir Google AdSense (Google Ireland Limited). Die Einwilligung wird
        über die zertifizierte Consent-Management-Plattform von Google (Funding Choices) eingeholt
        und verwaltet, einen eigenen Cookie-Banner betreiben wir nicht. Werbe-Cookies und damit
        verbundene Verarbeitungen werden erst nach deiner ausdrücklichen Einwilligung gemäß Art. 6
        Abs. 1 lit. a DSGVO gesetzt. Deine Einwilligung kannst du jederzeit über die von Googles CMP
        eingeblendete Schaltfläche „Datenschutzeinstellungen" bzw. „Einwilligung verwalten" auf der
        Website ändern oder widerrufen. Weitere Informationen findest du in der Datenschutzerklärung
        von Google unter https://policies.google.com/privacy.
      </Text>

      <Title order={2} mt="lg" mb="xs">
        Deine Rechte
      </Title>
      <List mb="md">
        <List.Item>Auskunft, Berichtigung und Löschung</List.Item>
        <List.Item>Einschränkung der Verarbeitung und Datenübertragbarkeit</List.Item>
        <List.Item>Widerspruch gegen die Verarbeitung und Widerruf erteilter Einwilligungen</List.Item>
        <List.Item>Beschwerde bei einer Aufsichtsbehörde</List.Item>
      </List>

      <Text size="sm" c="dimmed" mt="xl">
        Stand: Juni 2026.
      </Text>
    </Container>
  );
}
